import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { LandingPageHero } from '@/components/landing/LandingPageHero';
import { LandingPageContentSection } from '@/components/landing/LandingPageContentSection';
import { LandingPageFAQ } from '@/components/landing/LandingPageFAQ';
import { LandingPageCTA } from '@/components/landing/LandingPageCTA';
import { LandingPageBreadcrumb } from '@/components/landing/LandingPageBreadcrumb';
import {
  getBreadcrumbJsonLd,
  getFaqJsonLd,
  getServiceJsonLd,
  BASE_URL,
} from '@/lib/seo-constants';
import { Cog, Ruler, ShieldCheck, Truck, Factory, MapPin } from 'lucide-react';
import { useT, useLang } from '@/lib/i18n';

const PATH = '/cnc-machining-frankfurt';

/**
 * English-only geo landing page. The German variant is intentionally noindexed
 * to avoid duplicate-content cannibalisation with the existing /leistungen page.
 */
export default function CncMachiningFrankfurt() {
  const t = useT();
  const lang = useLang();

  const blocks = [
    { icon: Cog, title: 'CNC turning & milling', body: 'High-precision CNC turned and milled parts in steel, stainless steel, aluminium, titanium, brass and engineering plastics. 3 to 5 axis machining on HURCO and Emco machines.' },
    { icon: Ruler, title: 'Tolerances down to ±5 µm', body: 'Tight tolerances for mechanical engineering, medical devices, metrology, aerospace and energy. In-house measurement and ISO 9001:2015 quality management.' },
    { icon: Factory, title: 'Prototype to small series', body: 'From a single prototype to repeatable small and mid-volume series. Assemblies, fixtures and CAD/CAM design from the same shop floor.' },
    { icon: ShieldCheck, title: 'ISO 9001:2015 certified', body: 'Documented quality system, traceable material certificates, first-article inspection and CMM measurement reports on request.' },
    { icon: Truck, title: 'Fast logistics to Rhine-Main', body: 'Same-day pickup possible from Darmstadt for customers in Frankfurt, Offenbach, Hanau, Wiesbaden, Mainz, Aschaffenburg, Heidelberg and Mannheim.' },
    { icon: MapPin, title: 'Local partner, German engineering', body: '30 years of Mittelstand machining experience, family-owned, German-speaking project leads and English-speaking sales for international procurement teams.' },
  ];

  const faqs = [
    { question: 'Where is your CNC shop located?', answer: 'FERNAU Präzisionstechnik GmbH is located at Merianstraße 5a, 64291 Darmstadt, Germany — about 30 km south of Frankfurt am Main, directly in the Rhine-Main metropolitan area.' },
    { question: 'Do you serve customers in Frankfurt and the Rhine-Main area?', answer: 'Yes. We regularly supply manufacturers in Frankfurt, Offenbach, Hanau, Wiesbaden, Mainz, Aschaffenburg, Heidelberg and Mannheim. Same-day pickup from our Darmstadt shop is possible for urgent orders.' },
    { question: 'Which materials and tolerances can you machine?', answer: 'Steel, stainless steel, aluminium, titanium, brass, copper and engineering plastics. Standard tolerances down to ±5 µm depending on part geometry and material.' },
    { question: 'Are you ISO certified?', answer: 'Yes, FERNAU Präzisionstechnik is certified to DIN EN ISO 9001:2015. Material certificates and CMM measurement reports are available on request.' },
    { question: 'Do you handle prototypes as well as series production?', answer: 'Yes. We machine single prototypes, small series and mid-volume repeat production. CAD/CAM design and assembly work are available in-house.' },
    { question: 'In which language can we communicate?', answer: 'Project leads work in German and English. Drawings can be supplied as STEP, IGES, DXF or PDF.' },
  ];

  const jsonLd = [
    getBreadcrumbJsonLd([{ name: 'CNC machining near Frankfurt', path: PATH }], lang),
    getServiceJsonLd(
      {
        name: 'Precision CNC machining near Frankfurt',
        description:
          'High-precision CNC turning, milling and assemblies for manufacturers in the Frankfurt / Rhine-Main area. ISO 9001:2015 certified shop in Darmstadt, Germany.',
        url: `${BASE_URL}/en${PATH}`,
      },
      'en',
    ),
    getFaqJsonLd(faqs, 'en'),
  ];

  return (
    <Layout>
      <SEOHead
        title={{
          de: 'CNC-Zerspanung Frankfurt & Rhein-Main',
          en: 'Precision CNC Machining near Frankfurt | Rhine-Main, Germany',
        }}
        description={{
          de: 'CNC-Drehen und Fräsen für Kunden im Rhein-Main-Gebiet. ISO 9001 zertifizierter Lohnfertiger aus Darmstadt.',
          en: 'High-precision CNC turning, milling and assemblies near Frankfurt am Main. ISO 9001:2015 certified Mittelstand machining shop in Darmstadt / Rhine-Main, Germany.',
        }}
        path={PATH}
        jsonLd={jsonLd}
        noIndex={{ de: true }}
      />
      <LandingPageBreadcrumb current={t('CNC-Zerspanung Frankfurt', 'CNC machining near Frankfurt')} />
      <LandingPageHero
        badge={t('Rhein-Main', 'Rhine-Main, Germany')}
        title={t('Präzisions-CNC-Zerspanung', 'Precision CNC Machining')}
        highlight={t('im Rhein-Main-Gebiet', 'near Frankfurt am Main')}
        description={t(
          'CNC-Drehen, Fräsen und Baugruppenfertigung für Hersteller in Frankfurt, Offenbach, Hanau, Wiesbaden, Mainz und Aschaffenburg. ISO 9001 zertifiziert aus Darmstadt.',
          'CNC turning, milling and assembly manufacturing for industrial buyers in Frankfurt, Offenbach, Hanau, Wiesbaden, Mainz, Aschaffenburg, Heidelberg and Mannheim. ISO 9001:2015 certified, based in Darmstadt — 30 km south of Frankfurt.',
        )}
        ctaLabel={t('Anfrage senden', 'Request a quote')}
        ctaHref="/kontakt"
      />
      <LandingPageContentSection
        heading={t('Was wir für Kunden im Rhein-Main-Gebiet fertigen', 'What we machine for customers in the Rhine-Main area')}
        intro={t(
          'Vom Einzelteil bis zur wiederkehrenden Kleinserie. Hochpräzise Dreh- und Frästeile aus einer Familienhand seit 1995.',
          'From a single prototype to repeatable small-series production. High-precision turned and milled parts from a family-owned Mittelstand shop since 1995.',
        )}
        blocks={blocks}
      />
      <LandingPageFAQ faqs={faqs} />
      <LandingPageCTA
        heading={t('Brauchen Sie einen verlässlichen CNC-Partner nahe Frankfurt?', 'Need a reliable CNC partner near Frankfurt?')}
        description={t(
          'Senden Sie uns Ihre Zeichnung. Wir melden uns innerhalb eines Werktags mit einem belastbaren Angebot.',
          'Send us your drawing. We come back within one business day with a firm quotation.',
        )}
      />
    </Layout>
  );
}